import { useMemo, useState, useEffect } from 'react';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import {
  UK_YEAR_GROUPS,
  yearGroupsToDates,
  formatYearMonth,
  receptionStartYear,
} from '../lib/schoolYears';

/**
 * SchoolYearPicker
 * ----------------
 * Shown inside an "Add a school" form. Lets users pick year groups instead
 * of calendar dates when they can't remember exactly when they joined/left.
 *
 * Props:
 *  - dob: user's date of birth (YYYY-MM or YYYY-MM-DD) — required for inference
 *  - yearGroupStart, yearGroupEnd: current selections
 *  - startDate, endDate: current calendar-date strings (YYYY-MM)
 *  - onChange({ year_group_start, year_group_end, start_date, end_date }):
 *      called whenever the user changes anything. Parent merges into org state.
 *  - testIdPrefix: data-testid prefix for testing
 */
const SchoolYearPicker = ({
  dob,
  yearGroupStart,
  yearGroupEnd,
  startDate,
  endDate,
  onChange,
  testIdPrefix = 'school-yp',
}) => {
  const hasDob = !!receptionStartYear(dob);
  // Open by default if user has already selected a year group
  const [enabled, setEnabled] = useState(
    !!(yearGroupStart || yearGroupEnd),
  );

  useEffect(() => {
    if (yearGroupStart || yearGroupEnd) setEnabled(true);
  }, [yearGroupStart, yearGroupEnd]);

  const inferred = useMemo(
    () => yearGroupsToDates(dob, yearGroupStart, yearGroupEnd),
    [dob, yearGroupStart, yearGroupEnd],
  );

  const handleStart = (value) => {
    const next = yearGroupsToDates(dob, value, yearGroupEnd || value);
    onChange({
      year_group_start: value,
      year_group_end: yearGroupEnd || value,
      start_date: next.start_date || startDate || '',
      end_date: next.end_date || endDate || '',
    });
  };

  const handleEnd = (value) => {
    const next = yearGroupsToDates(dob, yearGroupStart, value);
    onChange({
      year_group_start: yearGroupStart || '',
      year_group_end: value,
      start_date: next.start_date || startDate || '',
      end_date: next.end_date || endDate || '',
    });
  };

  const handleToggle = (next) => {
    setEnabled(next);
    if (!next) {
      // Clearing year groups but keeping dates
      onChange({
        year_group_start: '',
        year_group_end: '',
        start_date: startDate || '',
        end_date: endDate || '',
      });
    }
  };

  return (
    <div
      className="rounded-lg bg-ground p-4 space-y-3"
      data-testid={`${testIdPrefix}-wrapper`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[15px] font-semibold text-ink">
            Not sure of exact dates? Use school year groups
          </p>
          <p className="text-sm text-ink-55 mt-1">
            Tell us the year group you joined and left, and we will estimate the
            dates for you.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          disabled={!hasDob}
          data-testid={`${testIdPrefix}-toggle`}
        />
      </div>

      {!hasDob && (
        <p
          className="text-sm text-amber-ink bg-amber-wash rounded-md px-3 py-2"
          data-testid={`${testIdPrefix}-no-dob-hint`}
        >
          Add your date of birth in step 1 first. We use it to work out the
          school years.
        </p>
      )}

      {enabled && hasDob && (
        <div className="space-y-3 pt-1">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">Which year group did you join in?</Label>
              <Select
                value={yearGroupStart || ''}
                onValueChange={handleStart}
              >
                <SelectTrigger
                  data-testid={`${testIdPrefix}-start`}
                >
                  <SelectValue placeholder="Select year group" />
                </SelectTrigger>
                <SelectContent>
                  {UK_YEAR_GROUPS.map((g) => (
                    <SelectItem key={g.value} value={g.value}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">Which year group did you leave in?</Label>
              <Select
                value={yearGroupEnd || ''}
                onValueChange={handleEnd}
              >
                <SelectTrigger
                  data-testid={`${testIdPrefix}-end`}
                >
                  <SelectValue placeholder="Select year group" />
                </SelectTrigger>
                <SelectContent>
                  {UK_YEAR_GROUPS.map((g) => (
                    <SelectItem key={g.value} value={g.value}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {(inferred.start_date || inferred.end_date) && (
            <p
              className="text-sm text-ink-70 bg-inset rounded-md px-3 py-2"
              data-testid={`${testIdPrefix}-inferred`}
            >
              Your dates are set as{' '}
              <span className="pd-mono text-ink">
                {formatYearMonth(inferred.start_date)} – {formatYearMonth(inferred.end_date)}
              </span>
              . You can change them if you need to.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SchoolYearPicker;
